import { addHours, isToday } from 'date-fns';
import { format } from 'date-fns-tz';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useLocation,
  useNavigationType,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import { RecommendationSource } from '~/api/Catalog';
import { company, DEFAULT_COMPANIES } from '~/company/Company';
import { formatVolume } from '~/components/ProductCard/utils';
import { useDateLang } from '~/hooks/useDateLang';
import { useOnWarehouseChange } from '~/hooks/useOnWarehouseChange';
import { useProductLimit } from '~/hooks/useProductLimit';
import { useStoreInstance } from '~/hooks/useStoreInstance';
import { type TabItem, useTabs } from '~/hooks/useTabs';
import ProductPageLocalee from '~/pages/Product/Localee/Product';
import { AnalyticsEvent, analyticsService } from '~/services/analytics';
import eventEmitter, { EventsName as BasicEventsName } from '~/services/Events';
import { cartStore, catalogStore, mainStore, orderStore } from '~/stores';
import { bottomNavigationStore } from '~/stores/BottomNavigationStore';
import {
  type Offer,
  type Product,
  ProductState,
} from '~/stores/CategoriesStore';
import { favoritesStore } from '~/stores/FavoritesStore/FavoritesStore';
import { isProductBundle } from '~/types/Product';
import type { ProductPropertiesValue } from '~/types/Product/interface';
import resetScroll from '~/utils/resetScroll';

import { ProductV3 } from './Citydrinks/ProductV3';

interface ProductPageLocation {
  state: {
    source?: string;
    isDeeplink?: boolean;
    clean_filters?: boolean;
    offerSku?: string;
  };
}

export interface ProductViewProps {
  tabs: Record<'product' | 'characteristics' | 'reviews', TabItem>;
  product: ProductState;
  isExpress: boolean;
  deliveryToday: boolean;
  backorderDate: Nullable<string>;
  isBackorderAvailable: Nullable<boolean>;
  isBundle: boolean;
  isMultiBuy: boolean;
  volumes: Array<{ id: string; value: string }>;
  currentItem: Nullable<Product | Offer>;
  currentPrice?: string;
  oldPrice: Nullable<string>;
  count: number;
  title?: string;
  limit: number;
  tab: Nullable<TabItem>;
  propertyList: ProductPropertiesValue[];
  badgeList: ProductPropertiesValue[];
  bundleImages: string[];
  isLoading?: boolean;
  handleCopyCode: () => Promise<void>;
  handleFavorite: () => void;
  addMultiBuyProductToCart: () => void;
  handleChangeSection: (alias: string) => void;
  handleChangeCount: (count: number, action: 'add' | 'remove') => void;
  handleChangeOffer: (sku: string) => () => void;
  className?: string;
}

const ProductPage = () => {
  const { t } = useTranslation();
  const { productId } = useParams<{ productId: string }>();
  const { state }: ProductPageLocation = useLocation();
  const product = useStoreInstance(ProductState, productId || '');
  const navType = useNavigationType();
  const { locale } = useDateLang();
  const [searchParams, setSearchParams] = useSearchParams();
  const trackedProductIdRef = useRef<number | null>(null);
  const [tab, setTab, tabs] = useTabs({
    product: {
      alias: 'product',
      title: t('product'),
    },
    characteristics: {
      alias: 'characteristics',
      title: t('characteristics'),
    },
    // TODO - rating
    // reviews: {
    //   alias: 'reviews',
    //   title: t('reviews'),
    // },
  });

  const isBundle = product.data ? isProductBundle(product.data) : false;

  const sku = searchParams.get('sku');
  if (sku && !isBundle) {
    if (product.data?.offers.find((offer) => offer.sku === sku)) {
      product.selectOffer(sku);
    }
  }

  const volumes = product.data?.offers?.map((item) => ({
    id: item.sku,
    value: formatVolume(item.properties),
  })) || [{ id: 'unknown', value: company.config.defaultVolumeDisplay }];

  const bundleImages = product.data?.images
    ? product.data.images
    : [`${product.data?.previewImageThumb}`].filter(Boolean);

  const currentItem = isBundle
    ? product.data
    : product.selectedOffer ||
      product.data?.offers.find((i) => i.sku === sku) ||
      product.data;
  const isMultiBuy =
    !isBundle && Boolean(product.selectedOffer?.multibuyPriceLevels?.length);

  const title = currentItem?.name;
  const currentPrice = currentItem?.discountPrice
    ? currentItem?.discountPriceFormatted
    : currentItem?.priceFormatted;
  const oldPrice =
    !isMultiBuy && currentItem?.discountPrice
      ? currentItem?.priceFormatted
      : null;

  const count = currentItem ? cartStore.getCartItemCountById(currentItem) : 0;

  const { limit, currentWhSellable, parentWhSellable } = useProductLimit({
    offer: currentItem,
  });

  useOnWarehouseChange(
    async (code) => {
      if (productId && !product.data && !product.request.isLoading) {
        bottomNavigationStore.setProductState(null);

        await product.request.run({ productId, warehouse: code });

        if (state?.offerSku) {
          product.selectOffer(state.offerSku);
        }

        bottomNavigationStore.setProductState(product);
      }
    },
    [productId, product],
  );

  useEffect(() => {
    if (!product.data) {
      return;
    }
    catalogStore.getProductRecommendationDebounced(
      product.data.offers.map((item) => item.sku),
      [
        ...(company.showSimilarProducts_ProductPage
          ? [RecommendationSource.CARD_SIMILAR]
          : []),
        ...(company.showFrequentlyBoughtTogether_ProductPage
          ? [RecommendationSource.FREQUENTLY_BOUGHT]
          : []),
      ],
    );

    return () => {
      catalogStore.clearProductRecommendations(
        RecommendationSource.CARD_SIMILAR,
      );
      catalogStore.clearProductRecommendations(
        RecommendationSource.FREQUENTLY_BOUGHT,
      );
    };
  }, [product.data]);

  useEffect(() => {
    if (navType !== 'POP') {
      resetScroll();
    }
  }, [JSON.stringify(tab)]);

  useEffect(() => {
    if (product.data && product.data.id !== trackedProductIdRef.current) {
      trackedProductIdRef.current = product.data.id;
      analyticsService.track(AnalyticsEvent.VIEW_ITEM, {
        currency: company.currency,
        value: product.data.discountPrice || product.data.price,
        items: [cartStore.convertToFirebase(product.data)],
      });

      analyticsService.track(AnalyticsEvent.PIXEL_VIEW_CONTENT, {
        content_name: product.data.name,
        content_ids: [product.data.id],
        content_type: 'product',
        currency: company.currency,
        value: mainStore.toFloat(product.data.priceFormatted),
      });
    }
  }, [product.data]);

  const handleChangeCount = (count: number, action: 'add' | 'remove') => {
    if (currentItem) {
      cartStore.setCartItemCountByProduct(
        currentItem,
        count,
        action,
        'product_main',
      );
    }
  };

  useEffect(() => {
    eventEmitter.on(BasicEventsName.ADD_ITEM_TO_CART, (data) => {
      handleChangeCount(data.count, data.type);
    });
    return () => {
      eventEmitter.off(BasicEventsName.ADD_ITEM_TO_CART);
    };
  }, [count, currentItem]);

  const handleFavorite = () => {
    if (!product.data && !product.ProductState) {
      return;
    }
    favoritesStore.toggleFavorite(
      product.data ?? product.ProductState,
      'product_main',
    );
    mainStore.sendToRN('hapticFeedback', {
      count: 1,
      type: 'soft',
    });
  };

  const handleChangeOffer = (sku: string) => () => {
    product.selectOffer(sku);
    searchParams.set('sku', sku);
    setSearchParams(searchParams, { replace: true });
  };

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(location.href);
      mainStore.pushAlert('success', t('linkCopied'));
    } catch (error) {
      error && console.error(error);
      mainStore.sendToRN(
        'copyToClipboard',
        location.href,
        () => {
          mainStore.pushAlert('success', t('referral:promoCopied'));
        },
        () => {
          mainStore.pushAlert('error', t('errors:unknown'));
        },
      );
    }
    analyticsService.track(AnalyticsEvent.REFERRAL_PROGRAM_COPY_CODE, {});
  };

  const addMultiBuyProductToCart = () => {
    if (!product.selectedOffer) {
      return;
    }

    const lowestLevel = product.selectedOffer.getLowestMultibuyLevel();
    if (!lowestLevel) {
      return;
    }

    const targetCount = lowestLevel.quantity - count;

    if (targetCount <= 0 || currentWhSellable < count + targetCount) {
      return;
    }

    handleChangeCount(count + targetCount, 'add');
  };

  const propertyList = Object.values(product.selectedOffer?.properties ?? {})
    .filter(
      ({ type, code }) =>
        type !== 'toggle' && code !== 'volume' && type !== 'badge',
    )
    .sort((a, b) => a.order - b.order);

  const badgeList = Object.values(product.selectedOffer?.properties ?? {})
    .filter(({ type, value }) => type === 'badge' && value === 'true')
    .sort((a, b) => a.order - b.order);

  const backorderDate = currentItem?.backorderLeadTime
    ? format(addHours(new Date(), currentItem.backorderLeadTime), 'd MMMM', {
        timeZone: company.config.timeZone,
        locale,
      })
    : null;

  const deliveryToday = product.selectedOffer?.backorderLeadTime
    ? isToday(addHours(new Date(), product.selectedOffer?.backorderLeadTime))
    : false;

  const isExpress =
    currentItem &&
    currentWhSellable &&
    currentWhSellable >= count &&
    orderStore.isExpressAvailableNow;

  const isBackorderAvailable =
    currentItem &&
    currentItem.isBackorderAvailable &&
    !!currentItem.backorderLeadTime &&
    parentWhSellable > 0;

  const Component = DEFAULT_COMPANIES.includes(company.name)
    ? ProductPageLocalee
    : ProductV3;

  return (
    <Component
      tabs={tabs}
      product={product}
      isExpress={!!isExpress}
      deliveryToday={deliveryToday}
      backorderDate={backorderDate}
      isBackorderAvailable={isBackorderAvailable}
      isBundle={isBundle}
      isMultiBuy={isMultiBuy}
      volumes={volumes}
      currentItem={currentItem}
      currentPrice={currentPrice}
      oldPrice={oldPrice}
      count={count}
      title={title}
      limit={limit}
      tab={tab}
      propertyList={propertyList}
      badgeList={badgeList}
      bundleImages={bundleImages}
      isLoading={
        String(product.data?.id) !== productId &&
        product.data?.slug !== productId
      }
      handleCopyCode={handleCopyCode}
      handleFavorite={handleFavorite}
      addMultiBuyProductToCart={addMultiBuyProductToCart}
      handleChangeSection={setTab}
      handleChangeCount={handleChangeCount}
      handleChangeOffer={handleChangeOffer}
    />
  );
};

export default observer(ProductPage);
